import * as React from 'react';
import { styled } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router';
import { Box, Button, Dialog, Divider, IconButton, Typography } from '@mui/material';
import Grid from '@mui/material/Grid';
import Close from '@mui/icons-material/Close';
import clsx from 'clsx';
import { Form, Formik, FormikHelpers } from 'formik';
import * as Yup from 'yup';
import { getLocalMerchantId } from 'components/settings/localStore';
import { getIdentityLabel } from 'components/settings/model/Settings';
import { setOnCloseRedirectUrl } from 'components/user/localAuth';
import { getIdentityValidationScheme } from 'components/user/model/User';
import { userApi } from 'components/user/userApi';
import { BottomDialog } from 'lib/BottomDialog';
import { TextFormField } from 'lib/form/TextFormField';
import { Link } from 'lib/Link';
import logger from 'lib/logger';
import { useAuth } from 'lib/useAuth';
import { useLocalHistory } from 'lib/useLocalHistory';
import { ROUTES } from 'pages/routes';
import { setReturnUrl, updateUser } from 'store/auth/authSlice';
import { setGuestContactInformation } from 'store/guest/guestSlice';
import { ApplicationState } from 'store/store';
import { LoadingButton } from 'ui/LoadingButton';
import { ViewBillHeader } from '../ViewBillHeader';
import { useSnackbar } from 'notistack';
import { EGAEventName, useGAHelpers } from 'lib/useGAHelpers';

const PREFIX = 'GuestCheckout';

const classes = {
    contentSpacing: `${PREFIX}contentSpacing`,
    content: `${PREFIX}content`,
    orCondition: `${PREFIX}orCondition`,
    guestCheckoutTitle: `${PREFIX}guestCheckoutTitle`,
    fullHeight: `${PREFIX}fullHeight`
};

const StyledBox = styled(Box)(({ theme }) => ({
    flex: 1,
    marginTop: theme.spacing(2),
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(0, 2, 2, 2),
    overflowY: 'scroll',
    [`& .${classes.orCondition}`]: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontWeight: theme.typography.fontWeightBold,
        color: theme.palette.grey[600],
        fontSize: theme.spacing(2),
        width: '56px',
        height: '56px',
        borderRadius: '50%',
        border: `1px solid ${theme.palette.divider}`
    },

    [`& .${classes.guestCheckoutTitle}`]: {
        margin: theme.spacing(0, 1)
    },

    [`& .${classes.fullHeight}`]: {
        height: '100%'
    }
}));

export interface IGuestCardPaymentFormValues {
    firstName: string;
    lastName: string;
    identity: string;
}

const initialValues: IGuestCardPaymentFormValues = {
    firstName: '',
    lastName: '',
    identity: ''
};

interface IProps {
    onClickBack: () => void;
    onAuthActionClick?: () => Promise<any>;
}

export const GuestCheckout: React.FC<React.PropsWithChildren<IProps>> = ({
    onClickBack,
    onAuthActionClick
}) => {
    const { user } = useAuth();
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { push } = useLocalHistory();
    const { pathname, search } = useLocation();
    const { enqueueSnackbar } = useSnackbar();

    const { settings } = useSelector((state: ApplicationState) => state.settings);
    const { identity } = useSelector((state: ApplicationState) => state.guest);
    const { logUserEvent } = useGAHelpers();

    const [alreadyHaveAccountOpen, setAlreadyHaveAccountOpen] = React.useState(false);
    // Email/phone is no longer required pre-payment — captured on the summary page instead.
    // The component is kept in the tree so it can be re-opened explicitly if a sign-in/up CTA
    // is added back to the basket later.
    const [isGuestCheckoutOpen, setIsGuestCheckoutOpen] = React.useState<boolean>(false);

    const userId = React.useMemo(() => (user ? user._id : ''), [user]);
    const identityLabel = React.useMemo(
        () => getIdentityLabel(settings?.emailAuthEnabled, settings?.phoneAuthEnabled),
        [settings?.emailAuthEnabled, settings?.phoneAuthEnabled]
    );
    const validationSchema = React.useMemo(
        () =>
            Yup.object().shape({
                firstName: Yup.string().required(t('FORMS_VALIDATION_REQUIRED')),
                lastName: Yup.string().required(t('FORMS_VALIDATION_REQUIRED')),
                identity: getIdentityValidationScheme(identityLabel, settings?.region.phoneNumberCode, t)
            }),
        [identityLabel, settings?.region?.phoneNumberCode, t]
    );
    const handleSubmit = React.useCallback(
        async (
            { firstName, lastName, identity: userIdentity }: IGuestCardPaymentFormValues,
            formikHelpers: FormikHelpers<IGuestCardPaymentFormValues>
        ) => {
            try {
                const [success, , guestDetails] = await userApi.updateGuestCredentials(userId, {
                    firstName,
                    lastName,
                    identity: userIdentity
                });
                dispatch(setGuestContactInformation({ firstName, lastName, identity: userIdentity }));
                if (!success) {
                    setAlreadyHaveAccountOpen(true);
                } else {
                    setIsGuestCheckoutOpen(false);
                    if (guestDetails) {
                        dispatch(updateUser(guestDetails));
                    }
                }
            } catch (error) {
                logger.error(error);
                enqueueSnackbar(t('GENERAL_ERROR_PROCESSING_REQUEST'), { variant: 'error' });
            } finally {
                formikHelpers.setSubmitting(false);
            }
        },
        [dispatch, enqueueSnackbar, t, userId]
    );
    const handleClearEmail = React.useCallback(
        (setFieldValue: FormikHelpers<any>['setFieldValue']) => () => {
            setFieldValue('identity', initialValues.identity);
        },
        []
    );
    const handleAlreadyHaveAnAccountClose = React.useCallback(() => {
        setAlreadyHaveAccountOpen(false);
        logUserEvent(EGAEventName.GuestAccountExistCancel);
    }, [logUserEvent]);

    const handleSignInClicked = React.useCallback(
        (event: React.MouseEvent<HTMLAnchorElement> | React.MouseEvent<HTMLButtonElement>) => {
            event.preventDefault();
            logUserEvent(EGAEventName.GuestAccountExistLogin);
            dispatch(setReturnUrl(pathname + search));
            const merchantId = getLocalMerchantId();
            if (merchantId) {
                setOnCloseRedirectUrl(pathname + search, merchantId);
            }
            if (onAuthActionClick) {
                onAuthActionClick();
            }
            if (identity) {
                push(ROUTES.USER.LOGIN, {}, `identity=${encodeURIComponent(identity)}`);
            } else {
                push(ROUTES.USER.LOGIN);
            }
        },
        [logUserEvent, pathname, search, dispatch, onAuthActionClick, identity, push]
    );
    const handleSignUpClicked = React.useCallback(() => {
        dispatch(setReturnUrl(pathname + search));

        const merchantId = getLocalMerchantId();
        if (merchantId) {
            setOnCloseRedirectUrl(pathname + search, merchantId);
        }
        if (onAuthActionClick) {
            onAuthActionClick();
        }
        push(ROUTES.USER.REGISTER);
    }, [pathname, search, dispatch, onAuthActionClick, push]);
    const handleBackClick = React.useCallback(() => {
        onClickBack();
        setIsGuestCheckoutOpen(false);
    }, [onClickBack]);

    return (
        <Dialog open={isGuestCheckoutOpen} fullScreen>
            <ViewBillHeader onBack={handleBackClick} title={t('GUEST_CHECKOUT_TITLE')} />
            <StyledBox
                className={clsx(classes.contentSpacing, classes.content)}
                flex={1}
                marginTop={2}
                display="flex"
                flexDirection="column"
            >
                <Typography align="center" variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                    {t('CHECKOUT_SIGN_IN_BENEFITS')}
                </Typography>
                <Box marginY={1}>
                    <Button color="primary" onClick={handleSignUpClicked} variant="contained" fullWidth>
                        {t('GUEST_CHECKOUT_SIGNUP')}
                    </Button>
                </Box>
                <Typography align="center" variant="body2">
                    {t('AUTH_ALREADY_HAVE_ACCOUNT') + ' '}
                    <Link onClick={handleSignInClicked} to="">
                        {t('ONBOARDING_SIGN_IN')}
                    </Link>
                </Typography>
                <Box marginTop={2} marginBottom={3} display="flex" justifyContent="center">
                    <div className={classes.orCondition}>{'OR'}</div>
                </Box>
                <Box display="flex" alignItems="center">
                    <Divider sx={{ flex: 1 }} />
                    <Typography className={classes.guestCheckoutTitle}>{t('GUEST_CHECKOUT')}</Typography>
                    <Divider sx={{ flex: 1 }} />
                </Box>
                <Box textAlign="center">
                    <Typography variant="caption">{t('GUEST_CHECKOUT_SUBTITLE')}</Typography>
                </Box>
                <Box flex={1}>
                    <Formik
                        onSubmit={handleSubmit}
                        initialValues={initialValues}
                        validationSchema={validationSchema}
                    >
                        {formik => {
                            const {
                                submitForm,
                                setFieldValue,
                                isSubmitting,
                                isValid,
                                values: { firstName, lastName, identity: userIdentity }
                            } = formik;

                            return (
                                <Form className={classes.fullHeight}>
                                    <Box paddingTop={2} display="flex" flexDirection="column" height="100%">
                                        <Grid container spacing={1}>
                                            <Grid size={6}>
                                                <TextFormField
                                                    name="firstName"
                                                    label={t('ONBOARDING_SIGNUP_FIRSTNAME')}
                                                    variant="standard"
                                                    trimWhiteSpaces="sides"
                                                />
                                            </Grid>
                                            <Grid size={6}>
                                                <TextFormField
                                                    name="lastName"
                                                    label={t('ONBOARDING_SIGNUP_LASTNAME')}
                                                    variant="standard"
                                                    trimWhiteSpaces="sides"
                                                />
                                            </Grid>
                                        </Grid>
                                        <Box marginY={2}>
                                            <TextFormField
                                                name="identity"
                                                type={identityLabel}
                                                label={t(`SIGN_UP_LABEL_${identityLabel.toUpperCase()}`)}
                                                InputProps={{
                                                    endAdornment: (
                                                        <IconButton
                                                            onClick={handleClearEmail(setFieldValue)}
                                                            size="small"
                                                            tabIndex={-1}
                                                        >
                                                            <Close />
                                                        </IconButton>
                                                    )
                                                }}
                                                variant="standard"
                                                trimWhiteSpaces="sides"
                                                description={t(
                                                    `CHECKOUT_GUEST_${identityLabel.toUpperCase()}_DESCRIPTION`
                                                )}
                                            />
                                        </Box>
                                        <Box marginTop="auto">
                                            <LoadingButton
                                                loading={isSubmitting}
                                                disabled={
                                                    isSubmitting ||
                                                    !isValid ||
                                                    !firstName ||
                                                    !lastName ||
                                                    !userIdentity
                                                }
                                                variant="contained"
                                                color="primary"
                                                fullWidth
                                                onClick={submitForm}
                                            >
                                                {t('GUEST_CHECKOUT_SUBMIT')}
                                            </LoadingButton>
                                        </Box>
                                    </Box>
                                </Form>
                            );
                        }}
                    </Formik>
                </Box>
            </StyledBox>
            <BottomDialog open={alreadyHaveAccountOpen}>
                <Grid container spacing={2}>
                    <Grid size={12}>
                        <Typography>{t('CHECKOUT_ACCOUNT_EXISTS')}</Typography>
                    </Grid>
                    <Grid size={6}>
                        <Button
                            variant="outlined"
                            color="primary"
                            onClick={handleAlreadyHaveAnAccountClose}
                            fullWidth
                        >
                            {t('GENERAL_CANCEL')}
                        </Button>
                    </Grid>
                    <Grid size={6}>
                        <Button variant="contained" color="primary" onClick={handleSignInClicked} fullWidth>
                            {t('ONBOARDING_SIGN_IN')}
                        </Button>
                    </Grid>
                </Grid>
            </BottomDialog>
        </Dialog>
    );
};
